import React, { useCallback, useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import urlJoin from 'url-join';
import { useQuery } from '@apollo/react-hooks';
import { useAuth0 } from 'Auth';
import {
  AnimatedScreen,
  LoadingPage,
  PageHeader,
  DEFAULT_RESULTS_PER_PAGE,
  Paginate,
  PaginationType,
  EmptyTable,
  Table,
  FormInput,
  UserStatusPill,
} from 'Shared/components';
import { Can } from 'Shared/utils/auth';
import { isMissing, isPresent, objectToParams } from 'Shared/utils/object';
import { createPartnerUserPolicy } from 'Admin/authPolicies';
import { PortalUserGrant } from 'Admin/Users/types';
import { getRoleFromGrants, PartnerContext } from 'Admin/utils';
import styles from './styles.module.css';
import { paramsFromSearchString } from 'Shared/utils';
import { Formik } from 'formik';
import { ClearFilterButton } from 'Shared/components/ClearFilterButtons';
import { GET_PARTNER_USERS_QUERY } from 'Admin/Partners/graphql';

export const userTableTestId = 'partner-users-table';

type Data = {
  users: {
    pagination: PaginationType;
    records: Array<{
      id: string;
      activated: boolean;
      firstName: string;
      lastName: string;
      email: string;
      licensedAgent: {
        nationalProducerNumber: string | null;
      } | null;
      portalUserGrants: Array<PortalUserGrant>;
    }>;
  };
};

type FilterType = {
  firstName?: string;
  lastName?: string;
  email?: string;
  nationalProducerNumber?: string;
};

const ALLOWED_FILTERS: Array<keyof FilterType> = [
  'firstName',
  'lastName',
  'email',
  'nationalProducerNumber',
];

export const PartnerUsers: React.FC<RouteComponentProps> = ({
  history,
  match,
}) => {
  const {
    loading: loadingPartner,
    error: partnerError,
    currentPartner,
  } = useContext(PartnerContext);
  const { user } = useAuth0();
  const [resultsPerPage, changeResultsPerPage] = useState(
    DEFAULT_RESULTS_PER_PAGE
  );
  const [filters, setFilters] = useState<FilterType>({});

  const { networkStatus, error, data, fetchMore } = useQuery<Data>(
    GET_PARTNER_USERS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        paginate: {
          limit: resultsPerPage,
          offset: 0,
        },
        partnerId: currentPartner && currentPartner.id,
        filters: filters,
      },
      skip:
        loadingPartner || isPresent(partnerError) || isMissing(currentPartner),
    }
  );

  useEffect(() => {
    const searchParams =
      history.location.search && history.location.search !== '?'
        ? paramsFromSearchString(history.location.search)
        : {};

    const allowedUrlFilters = ALLOWED_FILTERS.reduce((acc: FilterType, key) => {
      acc[key] = searchParams[key];
      return acc;
    }, {});

    const compactedFilters = compactFilters(allowedUrlFilters);

    setFilters(compactedFilters);
  }, [history.location.search]);

  const compactFilters = (newFilters: FilterType): FilterType => {
    return Object.entries(newFilters).reduce(
      (compacted: FilterType, [filterKey, filterValue]) => {
        if (filterValue) {
          return {
            ...compacted,
            [filterKey]: filterValue.trim(),
          };
        }

        return compacted;
      },
      {}
    );
  };

  const updateFilters = useCallback(
    (newFilters: FilterType) => {
      const compactedFilters = compactFilters(newFilters);
      history.replace({
        pathname: history.location.pathname,
        search: `?${objectToParams(compactedFilters)}`,
      });
      setFilters(compactedFilters);
    },
    [history]
  );

  const clearAllFilters = useCallback(
    () =>
      setFilters({
        firstName: '',
        lastName: '',
        email: '',
        nationalProducerNumber: '',
      }),
    [setFilters]
  );

  if (loadingPartner) return <LoadingPage />;
  const partnerName = currentPartner && currentPartner.name;

  const partnerUsers = data?.users?.records;
  const pagination = data?.users?.pagination;

  return (
    <AnimatedScreen
      testId="partner-users"
      loading={networkStatus === 1}
      error={error && error.message}
    >
      <Can
        grants={user!.effectiveGrants}
        policy={createPartnerUserPolicy}
        yes={() => (
          <PageHeader
            onActionClick={() => {
              history.push(`${history.location.pathname}/create`);
            }}
            actionName="Create User"
            actionTestId="create-user-button"
            heading={`Users of ${partnerName}`}
          />
        )}
        no={() => <PageHeader heading={`Users of ${partnerName}`} />}
      />

      <Formik
        enableReinitialize
        initialValues={filters}
        onSubmit={updateFilters}
      >
        {({ handleSubmit, values, handleChange }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className={styles.search}
              data-testid="form"
            >
              <div className={styles.inputContainer}>
                <FormInput
                  name="firstName"
                  className={styles.searchInput}
                  value={values.firstName}
                  label="Search first name"
                  variant="search"
                  onChange={handleChange}
                  onIconClick={() => {
                    handleSubmit();
                  }}
                />
                <FormInput
                  name="lastName"
                  className={styles.searchInput}
                  value={values.lastName}
                  label="Search last name"
                  variant="search"
                  onChange={handleChange}
                  onIconClick={() => {
                    handleSubmit();
                  }}
                />
                <FormInput
                  name="email"
                  className={styles.searchInput}
                  value={values.email}
                  label="Search email"
                  variant="search"
                  onChange={handleChange}
                  onIconClick={() => {
                    handleSubmit();
                  }}
                />
                <FormInput
                  name="nationalProducerNumber"
                  className={styles.searchInput}
                  value={values.nationalProducerNumber}
                  label="Search NPN"
                  variant="search"
                  onChange={handleChange}
                  onIconClick={() => {
                    handleSubmit();
                  }}
                />
              </div>
              <button type="submit" className={styles.submit} />
              <div className={styles.clearContainer}>
                <ClearFilterButton
                  onClick={clearAllFilters}
                  data-testid="clear-filters"
                  disabled={!Object.keys(filters).length}
                >
                  Clear Search
                </ClearFilterButton>
              </div>
            </form>
          );
        }}
      </Formik>

      {partnerUsers && partnerUsers.length ? (
        <>
          <Table
            name={userTableTestId}
            columnWidths={['118px', 2, 2, 3, 1, 2]}
            headers={[
              'Status',
              'First Name',
              'Last Name',
              'Email',
              'NPN',
              'Role(s)',
            ]}
            rows={partnerUsers.map((user) => ({
              key: user.id,
              onViewRow: () => history.push(urlJoin(match.url, user.id)),
              data: [
                <UserStatusPill activated={user.activated} />,
                user.firstName,
                user.lastName,
                user.email,
                user.licensedAgent?.nationalProducerNumber,
                getRoleFromGrants(user.portalUserGrants, currentPartner?.id),
              ],
            }))}
            loading={[2, 3, 4].includes(networkStatus)}
            hasPagination
          />
          <Paginate
            total={pagination?.total || 0}
            offset={pagination?.offset}
            limit={pagination?.limit}
            onOffsetChange={(limit, offset) => {
              fetchMore({
                variables: { paginate: { offset, limit } },
                updateQuery: (prev, { fetchMoreResult }) => {
                  if (!fetchMoreResult) return prev;
                  return {
                    ...prev,
                    ...fetchMoreResult,
                  };
                },
              });
            }}
            onLimitChange={changeResultsPerPage}
          />
        </>
      ) : (
        <EmptyTable>
          No users created for {partnerName || 'current partner'}
        </EmptyTable>
      )}
    </AnimatedScreen>
  );
};
